import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { categories } from '../../mocks/categories';

export default function RegisterProviderPage() {
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const data = new URLSearchParams();

    const fields = ['fullName', 'email', 'phone', 'serviceType', 'experience', 'description', 'dataConsent'];
    fields.forEach((field) => {
      const el = form.elements.namedItem(field) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (el) {
        if ((el as HTMLInputElement).type === 'checkbox') {
          data.append(field, (el as HTMLInputElement).checked ? 'Sí, acepto' : 'No acepta');
        } else {
          data.append(field, el.value);
        }
      }
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6smrf34rjv84lqbqghg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Ocurrió un error al enviar tu solicitud. Por favor, inténtalo de nuevo.');
      }
    } catch {
      setError('Ocurrió un error al enviar tu solicitud. Por favor, inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5c26f7fafee00d8c7af46082b5672344.jpg"
            alt="Ser Proveedor Task Zone"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-orange-900/80 via-orange-800/60 to-orange-700/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-white">
          <div className="max-w-2xl">
            <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-sm font-medium rounded-full mb-4 backdrop-blur-sm">
              <i className="ri-star-line mr-1.5"></i>
              Fase inicial de selección
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
              Únete como proveedor en Task Zone
            </h1>
            <p className="text-lg text-white/90 leading-relaxed">
              Estamos seleccionando profesionales independientes para ser parte de nuestra plataforma durante su fase inicial en Aguascalientes, México.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="flex-1 bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

            {/* Left: Info Column */}
            <div className="lg:col-span-2 space-y-8">
              {/* Intro block */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">¿Por qué unirte a Task Zone?</h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Task Zone es una plataforma diseñada para conectar a profesionales independientes con clientes en busca de servicios confiables. Al registrarte, amplías tu alcance y gestionas tu agenda de forma flexible.
                </p>
              </div>

              {/* Benefits */}
              <div className="space-y-4">
                {[
                  { icon: 'ri-user-star-line', title: 'Mayor visibilidad', desc: 'Llega a más clientes en tu área sin invertir en publicidad.' },
                  { icon: 'ri-calendar-2-line', title: 'Gestiona tu agenda', desc: 'Acepta solicitudes en los horarios que tú elijas.' },
                  { icon: 'ri-secure-payment-line', title: 'Cobros seguros', desc: 'Plataforma segura con historial de servicios.' },
                  { icon: 'ri-award-line', title: 'Construye tu reputación', desc: 'Acumula calificaciones y reseñas de clientes reales.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl">
                    <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg shrink-0">
                      <i className={`${item.icon} text-orange-600 text-xl`}></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                      <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Process steps */}
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-5">
                <h3 className="text-sm font-bold text-orange-800 mb-4 flex items-center gap-2">
                  <i className="ri-route-line"></i>
                  Proceso de selección
                </h3>
                <ol className="space-y-3">
                  {[
                    'Envías tu solicitud con los datos requeridos.',
                    'Task Zone evalúa tu perfil y compatibilidad.',
                    'Podemos solicitar información adicional o una breve entrevista.',
                    'Recibirás respuesta por correo oficial de Task Zone.',
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center bg-orange-600 text-white text-xs font-bold rounded-full shrink-0 mt-0.5">{i + 1}</span>
                      <span className="text-gray-700 text-xs leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="bg-white rounded-2xl p-10 text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                    <i className="ri-checkbox-circle-line text-green-600 text-4xl"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">¡Solicitud enviada con éxito!</h2>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-md mx-auto mb-6">
                    Hemos recibido tu información. El equipo de Task Zone revisará tu solicitud y se pondrá en contacto contigo únicamente a través de nuestras cuentas de correo oficiales.
                  </p>
                  <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 text-left max-w-sm mx-auto">
                    <p className="text-xs text-orange-800 font-medium flex items-start gap-2">
                      <i className="ri-information-line text-orange-600 mt-0.5 shrink-0"></i>
                      Revisa tu bandeja de entrada y carpeta de spam para no perder ninguna comunicación de Task Zone.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl overflow-hidden">
                  <div className="bg-orange-600 px-8 py-6">
                    <h2 className="text-xl font-bold text-white">Solicitud de proveedor</h2>
                    <p className="text-orange-100 text-sm mt-1">Completa el formulario para iniciar tu proceso de evaluación</p>
                  </div>

                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="px-8 py-8 space-y-5"
                  >
                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Nombre completo <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        required
                        placeholder="Ej. Carlos Ramírez"
                        className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    {/* Email + Phone */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Correo electrónico <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="correo@ejemplo.com"
                          className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Número de teléfono <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="+52 449 000 0000"
                          className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Service Type + Experience */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Tipo de servicio ofrecido <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="serviceType"
                          required
                          className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent cursor-pointer bg-white"
                        >
                          <option value="">Selecciona una opción</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Años de experiencia <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="experience"
                          required
                          className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent cursor-pointer bg-white"
                        >
                          <option value="">Selecciona</option>
                          <option value="Menos de 1 año">Menos de 1 año</option>
                          <option value="1 a 2 años">1 a 2 años</option>
                          <option value="3 a 5 años">3 a 5 años</option>
                          <option value="6 a 10 años">6 a 10 años</option>
                          <option value="Más de 10 años">Más de 10 años</option>
                        </select>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Descripción breve de tus servicios <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="description"
                        required
                        rows={4}
                        maxLength={500}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Describe brevemente los servicios que ofreces, tu especialidad y lo que te diferencia..."
                        className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                      />
                      <p className="mt-1 text-xs text-gray-400 text-right">{description.length}/500 caracteres</p>
                    </div>

                    {/* Checkbox */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="dataConsent"
                          required
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 mt-0.5 cursor-pointer shrink-0"
                        />
                        <span className="text-sm text-gray-600 leading-relaxed">
                          Autorizo el tratamiento de mis datos personales para efectos de evaluación como proveedor en la plataforma Task Zone, conforme a la{' '}
                          <a href="#" className="text-orange-600 hover:text-orange-700 underline">Política de Privacidad</a>.
                          <span className="text-red-500 ml-1">*</span>
                        </span>
                      </label>
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                        <i className="ri-error-warning-line text-red-500"></i>
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 px-6 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white font-semibold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Enviando solicitud...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          Enviar solicitud
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}

              {/* Formal Notice */}
              <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg shrink-0">
                    <i className="ri-file-text-line text-gray-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Aviso sobre el proceso de selección</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Léelo antes de enviar tu solicitud</p>
                  </div>
                </div>
                <div className="space-y-3 text-xs text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                  <p>
                    <strong className="text-gray-800">Evaluación:</strong> Una vez recibida tu solicitud, el equipo de Task Zone revisará la información proporcionada para verificar la compatibilidad con los estándares de la plataforma.
                  </p>
                  <p>
                    <strong className="text-gray-800">Proceso adicional:</strong> En algunos casos, es posible que se solicite información complementaria o se programe una breve entrevista para conocer mejor tu perfil profesional.
                  </p>
                  <p>
                    <strong className="text-gray-800">Sin garantía de aceptación:</strong> El envío de esta solicitud no garantiza la incorporación a la plataforma. Task Zone se reserva el derecho de declinar solicitudes durante esta fase piloto, sin necesidad de justificación detallada.
                  </p>
                  <p>
                    <strong className="text-gray-800">Comunicaciones oficiales:</strong> Todas las comunicaciones relacionadas con tu solicitud serán enviadas exclusivamente desde las cuentas de correo electrónico oficiales de Task Zone. Te recomendamos revisar también tu carpeta de correo no deseado (spam) para no perder ningún mensaje importante.
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
